/* Team (Area C): invitations, the organisation's 2FA policy, and the public accept-invitation flow. */
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { api } from '@/api/client'
import { authToken } from '@/api/token'
import type {
  AcceptInvitationBody,
  AcceptInvitationResult,
  Invitation,
  InvitationPreview,
  InviteBody,
  OrganizationSecurity,
} from '@/api/types'
import { can, type Action } from '@/lib/permissions'
import { i18n } from '@/i18n'
import { authKeys, sessionQuery, useSession } from './auth'
import { organizationKeys } from './organization'

export const teamKeys = {
  invitations: ['team', 'invitations'] as const,
  security: ['team', 'security'] as const,
  invite: (token: string) => ['team', 'invite', token] as const,
}

export const invitationsQuery = () =>
  queryOptions({
    queryKey: teamKeys.invitations,
    queryFn: ({ signal }) => api.get<Invitation[]>('/organization/invitations', { signal }),
  })

/** Pending invitations; polls while any is open so a simulated acceptance shows up. */
export function useInvitations(enabled = true) {
  const qc = useQueryClient()
  const q = useQuery({
    ...invitationsQuery(),
    enabled,
    refetchInterval: (q) => (q.state.data?.some((i) => i.status === 'pending') ? 8_000 : false),
  })
  // an invitation that stops being pending was most likely accepted: refresh the member list
  const pending = q.data?.filter((i) => i.status === 'pending').map((i) => i.id).join(',')
  const prev = useRef(pending)
  useEffect(() => {
    if (prev.current !== undefined && pending !== undefined && prev.current !== pending) {
      const was = prev.current.split(',').filter(Boolean)
      const now = new Set(pending.split(',').filter(Boolean))
      if (was.some((id) => !now.has(id))) void qc.invalidateQueries({ queryKey: organizationKeys.members })
    }
    prev.current = pending
  }, [pending, qc])
  return q
}

function useInvalidateTeam() {
  const qc = useQueryClient()
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: teamKeys.invitations }),
      qc.invalidateQueries({ queryKey: organizationKeys.members }),
    ])
}

export function useInvite() {
  const done = useInvalidateTeam()
  return useMutation({
    mutationFn: (body: InviteBody) => api.post<Invitation[]>('/organization/invitations', body),
    onSuccess: done,
    meta: { silent: true },
  })
}

export function useResendInvitation() {
  const done = useInvalidateTeam()
  return useMutation({
    mutationFn: (id: string) => api.post<Invitation>(`/organization/invitations/${id}/resend`),
    onSuccess: done,
    meta: { success: i18n.t('team:toasts.resent') },
  })
}

export function useRevokeInvitation() {
  const done = useInvalidateTeam()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/organization/invitations/${id}`),
    onSuccess: done,
    meta: { success: i18n.t('team:toasts.revoked') },
  })
}

/** The invitation link, to share it outside email. */
export function useInvitationLink() {
  return useMutation({
    mutationFn: (id: string) => api.get<{ url: string }>(`/organization/invitations/${id}/link`),
  })
}

export const orgSecurityQuery = () =>
  queryOptions({
    queryKey: teamKeys.security,
    queryFn: ({ signal }) => api.get<OrganizationSecurity>('/organization/security', { signal }),
  })

export function useOrgSecurity() {
  return useQuery(orgSecurityQuery())
}

export function useUpdateOrgSecurity() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { require2fa: boolean }) =>
      api.patch<OrganizationSecurity>('/organization/security', body),
    onSuccess: (data) => qc.setQueryData(teamKeys.security, data),
  })
}

/* ---------- the invitee's side (no sign-in needed) ---------- */

export function useInvitationPreview(token: string) {
  return useQuery({
    queryKey: teamKeys.invite(token),
    queryFn: ({ signal }) =>
      api.get<InvitationPreview>(`/invitations/${encodeURIComponent(token)}`, { signal, anonymous: true }),
    retry: false,
  })
}

export function useAcceptInvitation(token: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: AcceptInvitationBody) => {
      const r = await api.post<AcceptInvitationResult>(
        `/invitations/${encodeURIComponent(token)}/accept`,
        body,
        { anonymous: true },
      )
      if (r.status === 'signed_in') authToken.set(r.token)
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'auth' })
      await qc.fetchQuery({ ...sessionQuery(), staleTime: 0 })
      await qc.invalidateQueries({ queryKey: authKeys.session })
      return r
    },
    meta: { silent: true },
  })
}

/** `const allowed = useCan(); allowed('team.manage')`: the signed-in member's permissions (`src/lib/permissions.ts`). */
export function useCan() {
  const { data } = useSession()
  const role = data?.role
  return (action: Action) => can(role, action)
}
